import { useQuery } from '@tanstack/react-query';

import { FIRESTORE_ENABLED } from '@/config/env';
import { apiClient } from '@/lib/apiClient';
import { useFirestoreStream, type Filter } from './useFirestoreStream';

export interface LiveCollectionOptions<T> {
  path: string;
  httpPath: string;
  queryKey: readonly unknown[];
  parse: (id: string, raw: Record<string, unknown>) => T | null;
  limit: number;
  orderByField?: string;
  direction?: 'asc' | 'desc';
  filters?: readonly Filter[];
  enabled?: boolean;
}

export interface LiveCollection<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface ItemsResponse {
  items: ({ id: string } & Record<string, unknown>)[];
}

export function useLiveCollection<T>({
  path,
  httpPath,
  queryKey,
  parse,
  limit,
  orderByField,
  direction,
  filters,
  enabled = true,
}: LiveCollectionOptions<T>): LiveCollection<T> {
  const stream = useFirestoreStream<T>({
    path,
    parse,
    limit,
    orderByField,
    direction,
    filters,
    enabled: enabled && FIRESTORE_ENABLED,
  });

  const query = useQuery({
    queryKey,
    enabled: enabled && !FIRESTORE_ENABLED,
    queryFn: async () => {
      const response = await apiClient.get<ItemsResponse>(httpPath);
      return response.items
        .map(({ id, ...rest }) => parse(id, rest))
        .filter((row): row is T => row !== null);
    },
  });

  if (FIRESTORE_ENABLED) return { ...stream, refetch: () => undefined };

  return {
    data: query.data ?? [],
    loading: query.isPending && enabled,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}
